import { Dispatch } from 'redux';
import { world } from '../../../languages';
import session from '../../../utils/session';
import validator from '../../../utils/validator';
import { PAYMENT_METHOD_KEYS } from '../../../constants/payment-method-keys';
import { PaymentOptionType } from '../../../utils/enhanced-ecommerce';
import { FormDataType, FormErrorsType, FormInitialData } from '../components/purchase-form/purchase-form.constants';
import { ErrorMessageType } from '../components/error-message/error-message';

export const FORMDATA_SET = 'formData/SET';
export const FORM_ERRORS_SET = 'formErrors/SET';
export const FORM_ERRORS_RESET = 'formErrors/RESET';
export const STATE_SET = 'state/SET';

export type PaymentState = {
    isDataProcessing: boolean;
    formData: FormDataType;
    formSubmitAttempt: number;
    formErrors: FormErrorsType;
    isSchoolMaster: boolean;
    isPayButtonActive: boolean;
    failedPaymentMethods: PAYMENT_METHOD_KEYS[];
    withInvoice?: boolean;
    activePaymentOption?: PaymentOptionType;
    paymentErrors?: Map<number, ErrorMessageType>;
    paymentCart?: Cart;
    paymentOptionsAvailability?: Map<PAYMENT_METHOD_KEYS, boolean | null>;
};

const initialState: PaymentState = {
    isDataProcessing: true,
    formData: FormInitialData,
    formErrors: {},
    formSubmitAttempt: 0,
    isSchoolMaster: false,
    paymentErrors: new Map(),
    failedPaymentMethods: [],
    isPayButtonActive: true
};

const FIELDS_TO_CHECK = {
    CITY: 'city',
    EMAIL: 'email',
    FIRST_NAME: 'firstName',
    LAST_NAME: 'lastName',
    POSTCODE: 'postcode',
    STREET_ADDRESS: 'streetAddress',
    VAT_ID: 'vat_id'
};

type PaymentAction = {
    payload?: PaymentState;
    formData?: FormDataType;
    formErrors?: FormErrorsType;
    type: string;
};

export default (state = initialState, action: PaymentAction) => {
    switch (action.type) {
        case FORMDATA_SET:
            return {
                ...state,
                formData: {
                    ...state.formData,
                    ...action.formData
                }
            };
        case STATE_SET:
            return {
                ...state,
                ...action.payload
            };
        case FORM_ERRORS_RESET:
            return {
                ...state,
                formErrors: {}
            };
        case FORM_ERRORS_SET:
            return {
                ...state,
                formErrors: {
                    ...state.formErrors,
                    ...action.formErrors
                }
            };
        default:
            return state;
    }
};

const setupModel = (data: Partial<FormDataType>) => {
    Object.keys(data).forEach((k) => {
        if (k === 'type' && !data[k]) {
            data[k] = 'person';
        }

        if (k === 'gender' && !data[k]) {
            data[k] = 'female';
        }

        if (k === 'country' && !data[k]) {
            data[k] = world.toUpperCase();
        }

        if (!data[k]) {
            data[k] = '';
        }
    });

    return data;
};

export const changeState = (payload: Partial<PaymentState>) => (dispatch: Dispatch, getState: () => RootState) => {
    dispatch({
        payload,
        type: STATE_SET
    });
};

export const changeFormData = (data: Partial<FormDataType>) => (dispatch: Dispatch, getState: () => RootState) => {
    const formData = setupModel(data);

    dispatch({
        formData,
        type: FORMDATA_SET
    });
};

export const resetErrors = () => (dispatch: Dispatch) => {
    dispatch({
        type: FORM_ERRORS_RESET
    });
};

const formChecker = (state: PaymentState): FormErrorsType => {
    const { formData } = state;

    return {
        [FIELDS_TO_CHECK.EMAIL]:
            !validator.validateEmail(formData.email) || (!session.isLoggedIn() && state.isSchoolMaster),
        [FIELDS_TO_CHECK.CITY]: !formData.city.length,
        [FIELDS_TO_CHECK.LAST_NAME]: !formData.lastName.length,
        [FIELDS_TO_CHECK.FIRST_NAME]: !formData.firstName.length,
        [FIELDS_TO_CHECK.STREET_ADDRESS]: !formData.streetAddress.length,
        [FIELDS_TO_CHECK.POSTCODE]: !(function () {
            if (formData.country.toUpperCase() === 'DE' || !formData.country) {
                return formData.postcode.length === 5 && /^\d+$/.test(formData.postcode);
            }

            return formData.postcode.length >= 4;
        })(),
        [FIELDS_TO_CHECK.VAT_ID]: !validator.validateVATId(formData.vat_id, formData.country)
    };
};

export const validateField = (field: string) => (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState().payment;
    const { formErrors } = state;
    const obj = formChecker(state);

    dispatch({
        formErrors: {
            ...formErrors,
            [field]: Boolean(obj[field])
        },
        type: FORM_ERRORS_SET
    });
};

export const validateForm =
    (highlightInputs = true) =>
    (dispatch: Dispatch, getState: () => RootState) => {
        const state = getState().payment;
        const { activePaymentOption, withInvoice } = state;
        let formErrors: FormErrorsType = {};

        if (session.isLoggedIn()) {
            if (withInvoice || activePaymentOption?.id === 'Invoice') {
                formErrors = formChecker(state);

                delete formErrors.email;
            } else {
                formErrors = {};
            }
        }

        if (!session.isLoggedIn()) {
            if (withInvoice || activePaymentOption?.id === 'Invoice') {
                formErrors = formChecker(state);
            } else {
                formErrors = {
                    [FIELDS_TO_CHECK.EMAIL]: formChecker(state).email
                };
            }
        }

        if (highlightInputs) {
            dispatch({
                formErrors,
                type: FORM_ERRORS_SET
            });
        }

        return !Object.values(formErrors).some((item) => item);
    };

export const serializeForm = () => (dispatch: Dispatch, getState: () => RootState) => {
    const { formData, withInvoice } = getState().payment;

    if (withInvoice) {
        return formData;
    }

    return {
        email: formData.email
    };
};

export const addPaymentError = (error: ErrorMessageType) => (dispatch: Dispatch, getState: () => RootState) => {
    const { paymentErrors } = getState().payment;
    const newErrorMessage = error.message || error.reason;
    const errors = Array.from(paymentErrors?.values() ?? []);
    const isErrorAlreadyExists = errors.some(e => {
        const errorMessage = e.message || e.reason;

        return errorMessage === newErrorMessage;
    });

    if (isErrorAlreadyExists) {
        return;
    }

    const newErrorKey = ([...Array.from(paymentErrors?.keys() ?? [])].pop() ?? -1) + 1;

    paymentErrors?.set(newErrorKey, error);

    dispatch({
        payload: { paymentErrors },
        type: STATE_SET
    });
};

export const removePaymentError = (index: number) => (dispatch: Dispatch, getState: () => RootState) => {
    const { paymentErrors } = getState().payment;

    paymentErrors?.delete(index);

    dispatch({
        payload: { paymentErrors },
        type: STATE_SET
    });
};

export const resetPaymentErrors = () => (dispatch: Dispatch, getState: () => RootState) => {
    dispatch({
        payload: { paymentErrors: new Map() },
        type: STATE_SET
    });
};

export const addFailedPaymentMethod = (paymentMethod: PAYMENT_METHOD_KEYS, error?: string) => (dispatch: Dispatch, getState: () => RootState) => {
    const notUrgentErrors = [
        'Component could not mount. Root node was not found.',
        'Can not find element'
    ];

    // Skip some errors which not critical
    if (error && typeof error === 'string') {
        const isErrorNotUrgent = notUrgentErrors.some(e => error.includes(e));

        if (isErrorNotUrgent) {
            return;
        }
    }

    const { failedPaymentMethods } = getState().payment;

    let failedPaymentMethodsList = [paymentMethod];

    // Added all PP methods to the failed list even if only one PP method was failed
    if (
        [
            PAYMENT_METHOD_KEYS.PAYPAL_CHECKOUT,
            PAYMENT_METHOD_KEYS.PAYPAL_CHECKOUT_CREDIT_CARD,
            PAYMENT_METHOD_KEYS.PAYPAL_CHECKOUT_SEPA
        ].includes(paymentMethod)
    ) {
        failedPaymentMethodsList = [
            PAYMENT_METHOD_KEYS.PAYPAL_CHECKOUT,
            PAYMENT_METHOD_KEYS.PAYPAL_CHECKOUT_CREDIT_CARD,
            PAYMENT_METHOD_KEYS.PAYPAL_CHECKOUT_SEPA
        ];
    }

    failedPaymentMethods.push(...failedPaymentMethodsList);

    dispatch({
        payload: { failedPaymentMethods },
        type: STATE_SET
    });
};

export const submitForm = () => (dispatch: Dispatch, getState: () => RootState) => {
    const { formSubmitAttempt } = getState().payment;

    const nextAttempt = formSubmitAttempt + 1;

    dispatch({
        payload: { formSubmitAttempt: nextAttempt },
        type: STATE_SET
    });
};
